import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import '../../config/api'; // Import to ensure axios interceptors are configured
import { Module, MODULE_DISPLAY_INFO, useFeatureFlags } from '../../contexts/FeatureFlagContext';

import { DateField } from '../shared/DateField';

interface TenantLicense {
  id: number;
  tenantId: number;
  module: string;
  licenseTier: string | null;
  licenseType: string;
  status: string;
  source: string | null;
  packageCode: string | null;
  startDate: string;
  endDate: string | null;
  trialEndDate: string | null;
  createdAt: string;
}

interface PackageInfo {
  packageCode: string;
  displayName: string;
  description: string;
  productLine: string;
  tier: string;
  annualPriceUsd: number | null;
  includedModules: string[];
}

interface HistoryEntry {
  id: number;
  tenantId: number;
  module: string;
  action: string;
  oldStatus: string | null;
  newStatus: string | null;
  packageCode: string | null;
  actor: string;
  reason: string | null;
  createdAt: string;
}

interface AdminTenant {
  id: number;
  tenantCode: string;
  tenantName: string;
  clientType?: 'INSTITUTIONAL' | 'RETAIL' | 'HYBRID';
  product?: 'OMS' | 'COPILOT';
}

interface ModuleCatalogEntry {
  module: string;
  displayName: string;
  audience: 'SHARED' | 'INSTITUTIONAL' | 'RETAIL' | 'COPILOT';
  includedByDefault: boolean;
}

/** Which module audiences and package product lines a tenant should be offered. */
const offeringFor = (tenant?: AdminTenant) => {
  if (tenant?.product === 'COPILOT') {
    return { audiences: new Set(['SHARED', 'COPILOT']), productLines: new Set(['COPILOT']) };
  }
  if (tenant?.clientType === 'RETAIL') {
    return { audiences: new Set(['SHARED', 'RETAIL']), productLines: new Set(['RETAIL']) };
  }
  if (tenant?.clientType === 'INSTITUTIONAL') {
    return { audiences: new Set(['SHARED', 'INSTITUTIONAL']), productLines: new Set(['CREDIT', 'INSURANCE']) };
  }
  // HYBRID or unknown: offer everything
  return {
    audiences: new Set(['SHARED', 'INSTITUTIONAL', 'RETAIL', 'COPILOT']),
    productLines: new Set(['CREDIT', 'RETAIL', 'INSURANCE', 'COPILOT']),
  };
};

const LicenseManagementScreen: React.FC = () => {
  const { refreshLicenses } = useFeatureFlags();
  const [licenses, setLicenses] = useState<TenantLicense[]>([]);
  const [packages, setPackages] = useState<PackageInfo[]>([]);
  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogEntry[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [grantType, setGrantType] = useState<'package' | 'module' | 'trial'>('package');

  // Form state
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [trialDays, setTrialDays] = useState<number>(14);
  const [reason, setReason] = useState<string>('');

  const fetchLicenses = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/licenses/admin/tenant/${selectedTenantId}`);
      setLicenses(response.data);
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTenantId]);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`/api/licenses/admin/history/${selectedTenantId}`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch license history:', error);
      setHistory([]);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    fetchLicenses();
    fetchHistory();
  }, [fetchLicenses, fetchHistory]);

  useEffect(() => {
    const fetchStatic = async () => {
      try {
        const pkgResponse = await axios.get('/api/licenses/packages');
        setPackages(pkgResponse.data);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      }
      try {
        const catalogResponse = await axios.get('/api/licenses/modules/all');
        setModuleCatalog(catalogResponse.data);
      } catch (error) {
        console.error('Failed to fetch module catalog:', error);
      }
      try {
        const tenantResponse = await axios.get('/api/licenses/admin/tenants');
        if (Array.isArray(tenantResponse.data) && tenantResponse.data.length > 0) {
          setTenants(tenantResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch tenants:', error);
      }
    };
    fetchStatic();
  }, []);

  const afterMutation = () => {
    setShowGrantModal(false);
    setReason('');
    fetchLicenses();
    fetchHistory();
    refreshLicenses();
  };

  const handleGrantLicense = async () => {
    try {
      if (grantType === 'package') {
        await axios.post('/api/licenses/admin/apply-package', {
          tenantId: selectedTenantId,
          packageCode: selectedPackage,
          endDate: endDate || null,
          actor: 'admin-ui',
          reason: reason || 'Applied from license management screen'
        });
      } else if (grantType === 'module') {
        await axios.post('/api/licenses/admin/grant', {
          tenantId: selectedTenantId,
          module: selectedModule,
          endDate: endDate || null,
          createdBy: 'admin-ui'
        });
      } else if (grantType === 'trial') {
        await axios.post('/api/licenses/admin/grant-trial', {
          tenantId: selectedTenantId,
          module: selectedModule,
          trialDays: trialDays,
          createdBy: 'admin-ui'
        });
      }
      afterMutation();
    } catch (error) {
      console.error('Failed to grant license:', error);
    }
  };

  const handleRevokeLicense = async (module: string) => {
    if (!window.confirm(`Are you sure you want to revoke the ${module} license?`)) return;

    try {
      await axios.post('/api/licenses/admin/revoke', {
        tenantId: selectedTenantId,
        module: module
      });
      afterMutation();
    } catch (error) {
      console.error('Failed to revoke license:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return '#10b981';
      case 'TRIAL': return '#f59e0b';
      case 'EXPIRED': return '#ef4444';
      case 'SUSPENDED': return '#f97316';
      case 'CANCELLED': return '#6b7280';
      default: return '#94a3b8';
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'GRANTED':
      case 'PACKAGE_APPLIED':
      case 'TRIAL_STARTED':
      case 'TRIAL_CONVERTED': return '#10b981';
      case 'REVOKED':
      case 'PACKAGE_REMOVED':
      case 'SUSPENDED':
      case 'EXPIRED': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getModuleInfo = (module: string) => {
    return MODULE_DISPLAY_INFO[module as Module] || { name: module, icon: '📦', description: '' };
  };

  const formatPrice = (price: number | null) =>
    price != null ? `$${(price / 1000).toFixed(0)}K/yr` : 'Custom';

  const activePackageCodes = new Set(
    licenses.filter(l => l.status === 'ACTIVE' && l.packageCode).map(l => l.packageCode as string)
  );

  // Only offer what fits the selected tenant: an institutional client never
  // sees retail or Copilot packages/modules, and vice versa.
  const selectedTenant = tenants.find(t => t.id === selectedTenantId);
  const offering = offeringFor(selectedTenant);
  const visiblePackages = packages.filter(p => offering.productLines.has(p.productLine));
  const grantableModules = moduleCatalog.filter(
    m => !m.includedByDefault && offering.audiences.has(m.audience)
  );

  return (
    <Container>
      <Header>
        <Title>License Management</Title>
        <HeaderActions>
          <TenantSelect
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(Number(e.target.value))}
          >
            {tenants.length > 0 ? (
              tenants.map(t => (
                <option key={t.id} value={t.id}>{t.tenantName} (#{t.id})</option>
              ))
            ) : (
              <>
                <option value={1}>Tenant 1</option>
                <option value={2}>Tenant 2</option>
                <option value={3}>Tenant 3</option>
                <option value={4}>Tenant 4</option>
                <option value={6}>Tenant 6</option>
              </>
            )}
          </TenantSelect>
          <SecondaryButton onClick={() => setShowHistory(h => !h)}>
            {showHistory ? 'Hide History' : `History (${history.length})`}
          </SecondaryButton>
          <GrantButton onClick={() => setShowGrantModal(true)}>
            + Grant Access
          </GrantButton>
        </HeaderActions>
      </Header>

      <StatsRow>
        <StatCard>
          <StatValue>{licenses.filter(l => l.status === 'ACTIVE').length}</StatValue>
          <StatLabel>Active Licenses</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{licenses.filter(l => l.status === 'TRIAL').length}</StatValue>
          <StatLabel>Trial Licenses</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{licenses.filter(l => l.status === 'EXPIRED' || l.status === 'CANCELLED').length}</StatValue>
          <StatLabel>Expired / Cancelled</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{licenses.length}</StatValue>
          <StatLabel>Total Licenses</StatLabel>
        </StatCard>
      </StatsRow>

      {showHistory && (
        <Section>
          <SectionTitle>Entitlement History</SectionTitle>
          {history.length === 0 ? (
            <EmptyText>No entitlement changes recorded for this tenant.</EmptyText>
          ) : (
            <HistoryTable>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Module</th>
                  <th>Action</th>
                  <th>Status</th>
                  <th>Package</th>
                  <th>Actor</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td>{new Date(h.createdAt).toLocaleString()}</td>
                    <td>{getModuleInfo(h.module).name}</td>
                    <td><ActionBadge color={getActionColor(h.action)}>{h.action}</ActionBadge></td>
                    <td>{h.oldStatus ? `${h.oldStatus} → ` : ''}{h.newStatus ?? ''}</td>
                    <td>{h.packageCode ?? '—'}</td>
                    <td>{h.actor}</td>
                    <td>{h.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </HistoryTable>
          )}
        </Section>
      )}

      <Section>
        <SectionTitle>Current Licenses</SectionTitle>
        {isLoading ? (
          <LoadingText>Loading licenses...</LoadingText>
        ) : licenses.length === 0 ? (
          <EmptyText>No licenses found for this tenant. Apply a package to provision it.</EmptyText>
        ) : (
          <LicenseGrid>
            {licenses.map(license => {
              const moduleInfo = getModuleInfo(license.module);
              return (
                <LicenseCard key={license.id}>
                  <LicenseHeader>
                    <ModuleIcon>{moduleInfo.icon}</ModuleIcon>
                    <ModuleDetails>
                      <ModuleName>{moduleInfo.name}</ModuleName>
                      <ModuleCode>{license.module}</ModuleCode>
                    </ModuleDetails>
                    <StatusBadge color={getStatusColor(license.status)}>
                      {license.status}
                    </StatusBadge>
                  </LicenseHeader>
                  <LicenseBody>
                    <LicenseRow>
                      <LicenseLabel>Source:</LicenseLabel>
                      <LicenseValue>
                        {license.source === 'PACKAGE'
                          ? `Package · ${license.packageCode ?? ''}`
                          : license.source === 'TRIAL' ? 'Trial' : 'Add-on module'}
                      </LicenseValue>
                    </LicenseRow>
                    <LicenseRow>
                      <LicenseLabel>Start Date:</LicenseLabel>
                      <LicenseValue>{license.startDate}</LicenseValue>
                    </LicenseRow>
                    {license.endDate && (
                      <LicenseRow>
                        <LicenseLabel>End Date:</LicenseLabel>
                        <LicenseValue>{license.endDate}</LicenseValue>
                      </LicenseRow>
                    )}
                    {license.trialEndDate && (
                      <LicenseRow>
                        <LicenseLabel>Trial Ends:</LicenseLabel>
                        <LicenseValue>{license.trialEndDate}</LicenseValue>
                      </LicenseRow>
                    )}
                  </LicenseBody>
                  <LicenseActions>
                    <ActionButton
                      onClick={() => handleRevokeLicense(license.module)}
                      variant="danger"
                    >
                      Revoke
                    </ActionButton>
                  </LicenseActions>
                </LicenseCard>
              );
            })}
          </LicenseGrid>
        )}
      </Section>

      <Section>
        <SectionTitle>
          Available Packages
          {selectedTenant?.clientType && (
            <AudienceNote>
              showing {selectedTenant.product === 'COPILOT' ? 'Copilot' : selectedTenant.clientType.toLowerCase()} offerings
            </AudienceNote>
          )}
        </SectionTitle>
        <TierGrid>
          {visiblePackages.map(pkg => (
            <TierCard key={pkg.packageCode} highlighted={activePackageCodes.has(pkg.packageCode)}>
              <TierHeader>
                <div>
                  <TierName>{pkg.displayName}</TierName>
                  <TierProductLine>{pkg.productLine} · {pkg.tier}</TierProductLine>
                </div>
                <TierPrice>{formatPrice(pkg.annualPriceUsd)}</TierPrice>
              </TierHeader>
              {activePackageCodes.has(pkg.packageCode) && (
                <CurrentBadge>Current package</CurrentBadge>
              )}
              <TierModules>
                {pkg.includedModules.map(mod => {
                  const info = getModuleInfo(mod);
                  return (
                    <TierModule key={mod}>
                      <span>{info.icon}</span> {info.name}
                    </TierModule>
                  );
                })}
              </TierModules>
              <ApplyPackageButton onClick={() => {
                setGrantType('package');
                setSelectedPackage(pkg.packageCode);
                setShowGrantModal(true);
              }}>
                Apply to tenant
              </ApplyPackageButton>
            </TierCard>
          ))}
        </TierGrid>
      </Section>

      {showGrantModal && (
        <ModalOverlay onClick={() => setShowGrantModal(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>Grant Access</ModalTitle>

            <TabRow>
              <Tab
                active={grantType === 'package'}
                onClick={() => setGrantType('package')}
              >
                Package
              </Tab>
              <Tab
                active={grantType === 'module'}
                onClick={() => setGrantType('module')}
              >
                Module Add-on
              </Tab>
              <Tab
                active={grantType === 'trial'}
                onClick={() => setGrantType('trial')}
              >
                Trial
              </Tab>
            </TabRow>

            <FormGroup>
              {(grantType === 'module' || grantType === 'trial') && (
                <>
                  <Label>Module</Label>
                  <Select
                    value={selectedModule}
                    onChange={e => setSelectedModule(e.target.value)}
                  >
                    <option value="">Select a module...</option>
                    {grantableModules.map(m => (
                      <option key={m.module} value={m.module}>
                        {MODULE_DISPLAY_INFO[m.module as Module]?.name || m.displayName || m.module}
                      </option>
                    ))}
                  </Select>
                </>
              )}

              {grantType === 'package' && (
                <>
                  <Label>Package</Label>
                  <Select
                    value={selectedPackage}
                    onChange={e => setSelectedPackage(e.target.value)}
                  >
                    <option value="">Select a package...</option>
                    {visiblePackages.map(pkg => (
                      <option key={pkg.packageCode} value={pkg.packageCode}>
                        {pkg.displayName} ({formatPrice(pkg.annualPriceUsd)})
                      </option>
                    ))}
                  </Select>
                  <HelperText>
                    Applying a package grants every module in it. Modules granted by a previous
                    package that are no longer covered are revoked; add-on modules are untouched.
                  </HelperText>
                  <Label>Reason (audit trail)</Label>
                  <Input
                    type="text"
                    value={reason}
                    placeholder="e.g. Beach Point pilot — Credit Enterprise per contract"
                    onChange={e => setReason(e.target.value)}
                  />
                </>
              )}

              {grantType === 'trial' ? (
                <>
                  <Label>Trial Duration (days)</Label>
                  <Input
                    type="number"
                    value={trialDays}
                    onChange={e => setTrialDays(Number(e.target.value))}
                    min={1}
                    max={90}
                  />
                </>
              ) : (
                <>
                  <Label>End Date (optional)</Label>
                  <DateField
                    value={endDate ?? ''}
                    onChange={iso => setEndDate(iso)}
                  />
                </>
              )}
            </FormGroup>

            <ModalActions>
              <CancelButton onClick={() => setShowGrantModal(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={handleGrantLicense}>
                {grantType === 'package' ? 'Apply Package' : 'Grant License'}
              </ConfirmButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

// Styled Components

const Container = styled.div`
  padding: 2rem;
  min-height: 100vh;
  background: #0f172a;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #f1f5f9;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const TenantSelect = styled.select`
  background: #1e293b;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #f1f5f9;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
`;

const GrantButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #94a3b8;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #f1f5f9;
    border-color: rgba(148, 163, 184, 0.6);
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
`;

const StatValue = styled.div`
  color: #f1f5f9;
  font-size: 2rem;
  font-weight: 700;
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: #f1f5f9;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const AudienceNote = styled.span`
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 0.75rem;
  letter-spacing: 0.02em;
`;

const LoadingText = styled.div`
  color: #94a3b8;
  text-align: center;
  padding: 2rem;
`;

const EmptyText = styled.div`
  color: #94a3b8;
  text-align: center;
  padding: 2rem;
  background: rgba(30, 41, 59, 0.5);
  border-radius: 12px;
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  overflow: hidden;
  font-size: 0.85rem;

  th {
    text-align: left;
    color: #94a3b8;
    font-weight: 600;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(148, 163, 184, 0.2);
    background: rgba(15, 23, 42, 0.6);
  }

  td {
    color: #e2e8f0;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid rgba(148, 163, 184, 0.08);
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ActionBadge = styled.span<{ color: string }>`
  background: ${props => props.color}20;
  color: ${props => props.color};
  padding: 0.15rem 0.5rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
`;

const LicenseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
`;

const LicenseCard = styled.div`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  overflow: hidden;
`;

const LicenseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.1);
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const ModuleIcon = styled.div`
  font-size: 2rem;
`;

const ModuleDetails = styled.div`
  flex: 1;
`;

const ModuleName = styled.div`
  color: #f1f5f9;
  font-weight: 600;
`;

const ModuleCode = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  font-family: monospace;
`;

const StatusBadge = styled.span<{ color: string }>`
  background: ${props => props.color}20;
  color: ${props => props.color};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const LicenseBody = styled.div`
  padding: 1rem;
`;

const LicenseRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const LicenseLabel = styled.span`
  color: #94a3b8;
  font-size: 0.875rem;
`;

const LicenseValue = styled.span`
  color: #f1f5f9;
  font-size: 0.875rem;
`;

const LicenseActions = styled.div`
  padding: 1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ variant?: string }>`
  background: ${props => props.variant === 'danger' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
  color: ${props => props.variant === 'danger' ? '#ef4444' : '#3b82f6'};
  border: 1px solid ${props => props.variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.variant === 'danger' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'};
  }
`;

const TierGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const TierCard = styled.div<{ highlighted?: boolean }>`
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid ${props => props.highlighted ? 'rgba(16, 185, 129, 0.5)' : 'rgba(148, 163, 184, 0.2)'};
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const TierHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const TierName = styled.div`
  color: #f1f5f9;
  font-size: 1.15rem;
  font-weight: 600;
`;

const TierProductLine = styled.div`
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
`;

const TierPrice = styled.div`
  color: #3b82f6;
  font-size: 1.15rem;
  font-weight: 700;
  white-space: nowrap;
`;

const CurrentBadge = styled.div`
  color: #10b981;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const TierModules = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
`;

const TierModule = styled.div`
  color: #94a3b8;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ApplyPackageButton = styled.button`
  margin-top: 1rem;
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(59, 130, 246, 0.3);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #1e293b;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  color: #f1f5f9;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Tab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#3b82f6' : '#94a3b8'};
  border: 1px solid ${props => props.active ? 'rgba(59, 130, 246, 0.3)' : 'rgba(148, 163, 184, 0.2)'};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  color: #94a3b8;
  font-size: 0.875rem;
`;

const HelperText = styled.div`
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.4;
`;

const Select = styled.select`
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #f1f5f9;
  padding: 0.75rem;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Input = styled.input`
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  color: #f1f5f9;
  padding: 0.75rem;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  background: transparent;
  color: #94a3b8;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;

  &:hover {
    background: rgba(148, 163, 184, 0.1);
  }
`;

const ConfirmButton = styled.button`
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
`;

export default LicenseManagementScreen;
